import React, { useEffect, useRef, useState } from "react";
import { api, formatError, logDev } from "../../api";
import { FileText, FileX2, Loader2, ExternalLink, Download } from "lucide-react";

/**
 * ResumePreview — inline PDF preview for HR pipeline drawer.
 *
 * Fetches the resume blob via the authenticated `/applications/{appId}/resume/download`
 * endpoint, wraps it in a browser-generated ObjectURL, and mounts it in
 * an <iframe> so recruiters can read the resume without a new tab.
 *
 * Also exposes "Open in new tab" and "Download" side-actions for the
 * classic experience. Object URLs are revoked on unmount to avoid leaks.
 */
export default function ResumePreview({ appId, filename, hasResume = true }) {
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [err, setErr] = useState("");
  const urlRef = useRef(null);

  useEffect(() => {
    if (!hasResume || !appId) return;
    let cancelled = false;
    setState("loading");
    setErr("");
    (async () => {
      try {
        const r = await api.get(`/applications/${appId}/resume/download`, { responseType: "blob" });
        if (cancelled) return;
        const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        urlRef.current = url;
        setState("ready");
      } catch (e) {
        if (cancelled) return;
        // With responseType:"blob", axios wraps the JSON error body in a
        // Blob — parse it back to text so the recruiter sees the friendly
        // backend message (e.g. "resume was lost during a server restart —
        // please ask candidate to re-upload") instead of a generic
        // "Request failed with status 4xx".
        let msg = "";
        try {
          const blob = e?.response?.data;
          if (blob && typeof blob.text === "function") {
            const raw = await blob.text();
            try {
              const parsed = JSON.parse(raw);
              msg = parsed?.detail || parsed?.error || raw;
            } catch {
              msg = raw;
            }
          }
        } catch { /* fall through to formatError */ }
        if (!msg) msg = formatError(e);
        logDev.warn("[hr/resume-preview]", msg);
        setErr(msg);
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) {
        window.URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [appId, hasResume]);

  if (!hasResume) {
    return (
      <div className="text-center py-10" data-testid={`resume-preview-empty-${appId}`}>
        <FileX2 size={40} className="mx-auto text-muted mb-2" />
        <div className="text-sm text-muted">Candidate hasn't uploaded a resume.</div>
      </div>
    );
  }

  if (state === "loading" || state === "idle") {
    return (
      <div className="flex items-center justify-center py-10 gap-2 text-sm text-muted" data-testid={`resume-preview-loading-${appId}`}>
        <Loader2 size={16} className="animate-spin" /> Loading resume…
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid={`resume-preview-error-${appId}`}>
        Couldn't load the resume — {err}
      </div>
    );
  }

  const openTab = () => {
    if (urlRef.current) window.open(urlRef.current, "_blank", "noopener,noreferrer");
  };

  const download = () => {
    if (!urlRef.current) return;
    const a = document.createElement("a");
    a.href = urlRef.current;
    a.download = filename || `resume-${appId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`resume-preview-${appId}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-xs text-slate-700 min-w-0">
          <FileText size={13} className="text-signal shrink-0" />
          <span className="truncate font-medium">{filename || "resume.pdf"}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={openTab}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border bg-white text-signal hover:bg-paper transition"
            style={{ borderColor: "rgb(0 108 255 / 0.3)" }}
            data-testid={`resume-preview-open-${appId}`}
          >
            <ExternalLink size={11} /> New tab
          </button>
          <button
            type="button"
            onClick={download}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md border bg-white text-slate-700 hover:bg-paper transition"
            style={{ borderColor: "var(--rule)" }}
            data-testid={`resume-preview-download-${appId}`}
          >
            <Download size={11} /> Download
          </button>
        </div>
      </div>
      <iframe
        title={`Resume — ${filename || appId}`}
        src={urlRef.current}
        className="w-full rounded-xl border bg-white"
        style={{ height: "70vh", borderColor: "var(--rule)" }}
        data-testid={`resume-preview-iframe-${appId}`}
      />
    </div>
  );
}
